import React, { useRef, useState } from 'react';
import { chatService } from '../../services/chatService';

interface RfiFileUploadProps {
  onFileUploaded: (questions: any[], threadId: string) => void;
}

const RfiFileUpload: React.FC<RfiFileUploadProps> = ({
  onFileUploaded
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadStatus('');
    }
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadStatus('Uploading...');
    
    try {
      const response = await chatService.uploadRfiFile(selectedFile);
      setUploadStatus('File uploaded successfully!');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Extract and store questions from response
      if (response.questions && Array.isArray(response.questions)) {
        onFileUploaded(response.questions, response.thread_id || '');
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
      setUploadStatus('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadStatus('');
    }
  };

  return (
    <div className="card-elevated mb-6">
      <h3 className="text-xl font-semibold mb-6 text-gray-800">Upload RFI File Here</h3>
      <p className="text-gray-600 mb-8">Choose a file</p>
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="*/*"
      />
      
      <div 
        className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-adalabs-blue-400 transition-colors cursor-pointer mb-8"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <p className="text-gray-600">
          {selectedFile ? `Selected: ${selectedFile.name}` : 'Drag and drop files here'}
        </p>
      </div>
      
      <div className="space-y-4">
        <button 
          className="btn-secondary px-6 py-2 text-sm"
          onClick={handleBrowseFiles}
        >
          Browse files
        </button>
        
        {selectedFile && (
          <button 
            className="btn-accent px-6 py-2 text-sm ml-2"
            onClick={handleFileUpload}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </button>
        )}
      </div>
      
      {uploadStatus && (
        <div className={`mt-6 p-4 rounded-lg ${
          uploadStatus.includes('success') 
            ? 'bg-green-100 border border-green-300 text-green-800' 
            : 'bg-red-100 border border-red-300 text-red-800'
        }`}>
          {uploadStatus}
        </div>
      )}
    </div>
  );
};

export default RfiFileUpload;
